import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, Receipt } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { type PageSize } from '@/hooks/useCrudList'
import type { PaginationMeta } from '@/types/api'
import { toast } from '@/components/ui/Toast'
import { brl, dateBR } from '@/lib/format'
import { MyCompanyService } from '@/services/modules/MyCompanyService'
import type { Revenue } from '@/services/modules/RevenuesService'

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'PENDENTE', label: 'Pendente' },
  { value: 'PAGO', label: 'Pago' },
  { value: 'CANCELADO', label: 'Cancelado' },
  { value: 'ISENTO', label: 'Isento' },
]

export function MyCompanyInvoicesPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<Revenue[]>([])
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [size, setSize] = useState<PageSize>(25)
  const [status, setStatus] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await MyCompanyService.invoices({ page, size, ...(status ? { status } : {}) })
      setRows(res.data)
      setMeta(res.meta)
    } catch { toast.error('Erro ao carregar as faturas.') }
    finally { setLoading(false) }
  }, [page, size, status])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(1) }, [status, size])

  const columns: DataTableColumn<Revenue>[] = [
    { key: 'dueDate',  header: 'Vencimento', align: 'center', render: r => r.formatted?.dueDate ?? dateBR(r.dueDate) },
    { key: 'paidDate', header: 'Pagamento',  align: 'center', render: r => r.paidDate ? (r.formatted?.paidDate ?? dateBR(r.paidDate)) : '—' },
    { key: 'amount',   header: 'Total',      align: 'right',  render: r => <span className="font-medium text-gray-800">{r.formatted?.amount ?? brl(r.amount)}</span> },
    { key: 'method',   header: 'Forma de pagamento', align: 'center', render: r => r.method ?? '—' },
    { key: 'status',   header: 'Status', align: 'center', render: r => <StatusBadge status={r.status} /> },
  ]

  return (
    <>
      <PageHeader title="Faturas" subtitle="Faturas da sua empresa"
        crumbs={[{ label: 'Financeiro' }, { label: 'Faturas' }]}
      />

      <div className="mb-4 flex items-center gap-3 rounded-md bg-white p-4 ring-1 ring-gray-200">
        <div className="w-56">
          <Select value={status} onChange={e => setStatus(e.target.value)} options={STATUS_OPTIONS} searchable={false} />
        </div>
        <span className="ml-auto text-sm text-gray-500">{meta?.total ?? 0} fatura(s)</span>
      </div>

      <DataTable
        tableId="my-company.invoices"
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={r => r.id}
        emptyMessage="Nenhuma fatura encontrada."
        rowActions={r => [
          { key: 'view', label: 'Ver detalhes', icon: <Eye size={14} />, onClick: () => navigate(`/my-company/invoices/${r.id}`) },
          ...(r.bankSlipUrl ? [{
            key: 'boleto', label: 'Ver boleto', icon: <Receipt size={14} />,
            onClick: () => window.open(r.bankSlipUrl!, '_blank', 'noopener,noreferrer'),
          }] : []),
        ]}
      />
      <Pagination meta={meta} size={size} onChange={setPage} onSizeChange={setSize} />
    </>
  )
}

export default MyCompanyInvoicesPage
